import React from "react";
import { FormItem } from "@/components/ui/form";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { InfoIcon } from "lucide-react";

interface Option {
  value: string;
  key?: string;
}

interface SimplifiedSelectFieldProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  label: string;
  description?: string;
  required?: boolean;
  videoUrl?: string;
  options: Option[];
  multiline?: boolean;
}

export const SimplifiedSelectField: React.FC<SimplifiedSelectFieldProps> = ({
  value,
  onChange,
  label,
  description,
  required = false,
  videoUrl,
  options,
  multiline = false
}) => {
  // Para múltipla seleção
  const handleCheckboxChange = (optionValue: string) => {
    if (multiline) {
      const currentValues = Array.isArray(value) ? [...value] : [];
      if (currentValues.includes(optionValue)) {
        onChange(currentValues.filter(v => v !== optionValue));
      } else {
        onChange([...currentValues, optionValue]);
      }
    }
  };

  // Para seleção única
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  if (multiline) {
    // Múltipla seleção com checkboxes
    return (
      <div className="w-full">
        <div className="flex items-center mb-2">
          <label className="text-slate-500 font-bold px-2">
            {label}{required && <span className="text-red-500">*</span>}
          </label>
          {description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger><InfoIcon size={16} className="text-grey-light ml-1" /></TooltipTrigger>
                <TooltipContent>
                  <p>{description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="space-y-2 pl-2">
          {options.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox 
                id={`${label}-${option.value}`}
                checked={Array.isArray(value) ? value.includes(option.value) : false}
                onCheckedChange={() => handleCheckboxChange(option.value)}
              />
              <label 
                htmlFor={`${label}-${option.value}`}
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {option.key || option.value}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Seleção única com dropdown
  return (
    <div className="w-full">
      <div className="flex items-center mb-2">
        <label className="text-slate-500 font-bold px-2">
          {label}{required && <span className="text-red-500">*</span>}
        </label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger><InfoIcon size={16} className="text-grey-light ml-1" /></TooltipTrigger>
              <TooltipContent>
                <p>{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <select
        value={value as string}
        onChange={handleSelectChange}
        className="w-full p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="" disabled>Selecione uma opção</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.key || option.value}
          </option>
        ))}
      </select>
    </div>
  );
}; 